import React from 'react';
import Skeleton from 'react-loading-skeleton';
import {
  AuctionContractIcon,
  BodyText,
  FlexRow,
} from '@make-software/cspr-design';
import {
  AccountCentralizedInfo,
  AccountInfoResult,
  AccountModel,
  DeployTransferResult,
} from '@block-explorer/entities';
import {
  HashFontSize,
  HistoryLink,
  TableDataAccountInfo,
} from '../../../components';
import { TableDataNamedKey } from '../../table-data-named-key/table-data-named-key';
import { buildRouterPath } from '../../../router/utils';
import { ACCOUNT_DETAILS_PATH } from '../../../router/paths';
import Avatar from '../../../components/avatar/avatar';
import DeployFiatAmount from './DeployFiatAmount';
import { deriveAccountInfo } from '../../../utils/account';
import { useDeployActionDataContext } from '../services/deploy-action-context';
import { Tooltip } from '@block-explorer/ui';
import { useTranslation } from 'react-i18next';

interface TransferActionAccountProps {
  publicKey?: string | null;
  accountHash?: string | null;
  loading: boolean;
  purse: string;
  renderAsResultAction: boolean;
  actionPoolAccountHash?: string;
  purseAccountInfoFromTransfer?:
    | AccountInfoResult
    | AccountCentralizedInfo
    | null;
}

const TransferActionAccount = ({
  publicKey,
  accountHash,
  loading,
  purse,
  renderAsResultAction,
  actionPoolAccountHash,
  purseAccountInfoFromTransfer,
}: TransferActionAccountProps) => {
  const { t } = useTranslation();
  const { getAccountInfo } = useDeployActionDataContext();

  const accountInfo = getAccountInfo<AccountInfoResult>(
    publicKey || accountHash || ''
  );
  const accountInfoDetails = deriveAccountInfo(
    accountInfo?.account_info ||
      accountInfo?.centralized_account_info ||
      purseAccountInfoFromTransfer
  );

  const logo = accountInfoDetails && accountInfoDetails?.logo;
  const name = accountInfoDetails?.name;
  const csprName = accountInfo?.cspr_name;

  const isAuctionPoolAccount = accountHash === actionPoolAccountHash;

  if (isAuctionPoolAccount && renderAsResultAction) {
    return (
      <FlexRow itemsSpacing={8} align={'center'}>
        <Avatar
          size={'small'}
          path={AuctionContractIcon}
          loading={loading}
          alt={'Auction contract logo'}
        />
        <Tooltip
          tooltipContent={actionPoolAccountHash}
          caption={t('Account Hash')}
        >
          <BodyText size={3} monotype>
            <HistoryLink
              href={buildRouterPath(
                ACCOUNT_DETAILS_PATH,
                { id: actionPoolAccountHash || '' },
                undefined
              )}
              ariaDescription={'Link to account details'}
            >
              Auction Pool
            </HistoryLink>
          </BodyText>
        </Tooltip>
      </FlexRow>
    );
  }

  // TODO: REPLACE WITH ADDRESS LATER
  return publicKey || accountHash ? (
    <TableDataAccountInfo
      logo={logo}
      name={name}
      publicKey={publicKey || accountHash}
      csprName={csprName}
      loading={loading}
      avatarSize={'small'}
      hashFontSize={HashFontSize.big}
    />
  ) : (
    <TableDataNamedKey purse={purse} fontSize={'sm'} />
  );
};

interface TransferActionRowProps {
  timeTransactionCurrencyRate: number;
  transfer: DeployTransferResult;
  loading: boolean;
  actionPoolAccountHash: string | undefined;
  renderAsResultAction?: boolean;
}

const TransferActionRow = ({
  timeTransactionCurrencyRate,
  transfer,
  loading,
  actionPoolAccountHash,
  renderAsResultAction = false,
}: TransferActionRowProps) => {
  const fromAccountHash = transfer.from_purse_public_key
    ? AccountModel({
        publicKeyHex: transfer.from_purse_public_key,
      }).getAccountHash()
    : '';

  if (loading) {
    return (
      <BodyText size={3}>
        <Skeleton />
      </BodyText>
    );
  }

  const deployActionText = renderAsResultAction ? 'Transferred' : 'Transfer';

  return (
    <FlexRow align={'center'} itemsSpacing={8} wrap={'nowrap'}>
      <BodyText size={3} monotype variation={'black'}>
        {deployActionText}
      </BodyText>{' '}
      <DeployFiatAmount
        amount={transfer.amount}
        rate={timeTransactionCurrencyRate}
      />
      {renderAsResultAction && (
        <>
          <BodyText size={3} variation="darkGray">
            from
          </BodyText>
          <BodyText size={3} monotype variation="black">
            <TransferActionAccount
              publicKey={transfer.from_purse_public_key}
              accountHash={
                transfer.from_purse_public_key ? fromAccountHash : null
              }
              purse={transfer.from_purse}
              purseAccountInfoFromTransfer={
                transfer.from_purse_account_info ||
                transfer.from_purse_centralized_account_info
              }
              loading={loading}
              renderAsResultAction={renderAsResultAction}
              actionPoolAccountHash={actionPoolAccountHash}
            />
          </BodyText>
        </>
      )}
      <BodyText size={3} variation="darkGray">
        to
      </BodyText>
      <BodyText size={3} monotype variation="black">
        <TransferActionAccount
          publicKey={transfer.to_purse_public_key}
          accountHash={transfer.to_account_hash}
          purse={transfer.to_purse}
          purseAccountInfoFromTransfer={
            transfer.to_purse_account_info ||
            transfer.to_purse_centralized_account_info
          }
          loading={loading}
          renderAsResultAction={renderAsResultAction}
          actionPoolAccountHash={actionPoolAccountHash}
        />
      </BodyText>
    </FlexRow>
  );
};
export default TransferActionRow;
